import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { ApiError } from "../types";
import { purgeCdnCache } from "./nft-cache";

const API_URL = import.meta.env.VITE_API_URL;

interface BatchFileUploadResponseDto {
	files: {
		url: string;
		key: string;
	}[];
}

export const useUploadNFTImagesMutation = () => {
	const queryClient = useQueryClient();
	return useMutation<
		BatchFileUploadResponseDto,
		ApiError,
		{ files: File[]; collectionAddress: string }
	>({
		mutationFn: async ({ files, collectionAddress }) => {
			const formData = new FormData();
			formData.append("collectionAddress", collectionAddress);

			for (const file of files) {
				formData.append("files", file);
			}

			const response = await fetch(`${API_URL}/inventory/images/upload`, {
				method: "POST",
				credentials: "include",
				body: formData,
			});

			if (!response.ok) {
				const error = await response.json();
				throw error;
			}

			return (await response.json()).data;
		},
		onSuccess: async (data, variables) => {
			localStorage.setItem("cache-bust", Date.now().toString());
			await purgeCdnCache({
				keys: variables.files.map(
					(file) => `${variables.collectionAddress}/images/${file.name}.png`,
				),
			});
			await Promise.all(
				data.files.map(async (file) => {
					await queryClient.invalidateQueries({
						queryKey: ["image", file.url],
					});
				}),
			);
			toast.success("Images uploaded successfully");
		},
		onError: (error) => {
			toast.error(error.message);
		},
	});
};
